import SvgIcon from "@/components/common/SvgIcon";
import {
  IconButton,
  Popover,
  PopoverContent,
  PopoverHandler,
} from "@material-tailwind/react";
import { Drawer as DrawerMui } from "@material-tailwind/react/components/Drawer";
import { onClose } from "@material-tailwind/react/types/components/alert";
import classNames from "classnames";
import { memo, ReactNode, useEffect, useMemo, useState } from "react";
import useGetMediaQuery from "@/hooks/general/useGetMediaQuery.ts";
import { update, showArticle } from "@intercom/messenger-js-sdk";
import { Button, Typography } from "..";

export type TProps = {
  open: boolean;
  title: ReactNode | string;
  description?: string;
  onClose: onClose;
  children: ReactNode;
  className?: string;
  customHeader?: ReactNode;
  isFullScreen?: boolean;
  overlayClassName?: string;
  link?: number;
  hasControl?: boolean;
};

function Drawer({
  open,
  onClose,
  title,
  children,
  description,
  className,
  customHeader,
  link,
  isFullScreen = false,
  overlayClassName,
  hasControl = true,
}: Readonly<TProps>) {
  const [isMaxW, setIsMaxW] = useState(false);
  const handleMaximize = () => setIsMaxW((pre) => !pre);
  const { mediaQuery, isDown, isUp } = useGetMediaQuery();
  const size = useMemo<string>(() => {
    if (!open) return "";

    if (isDown("2xl")) return "!max-w-[calc(100vw_-_48px)]";

    if (isMaxW || isFullScreen) {
      return `!w-full !max-w-[calc(100vw_-_48px)]`;
    }
    return "!max-w-[67vw]";
  }, [mediaQuery, open, isMaxW]);

  useEffect(() => {
    update({ hide_default_launcher: open });
  }, [open]);

  return (
    <DrawerMui
      placeholder=""
      open={open}
      onClose={onClose}
      overlay
      overlayProps={{
        className: classNames(
          "fixed duration-0 !backdrop-blur-0 h-[100vh] bg-black/60 opacity-100 drawer-overlay-background",
          {
            ["!hidden"]: !open,
          },
          overlayClassName,
        ),
      }}
      placement="right"
      className={classNames(
        `dark:bg-shades-dark duration-[180ms] !ease-linear`,
        className,
        {
          "!shadow-none !duration-0": !open,
          [size]: open,
        },
      )}
      onClick={() => {}}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={classNames({
          hidden: !open,
        })}
      >
        {(!customHeader && (
          <div className="px-6 py-3 flex items-center">
            <div className="flex items-center gap-2">
              <div className={"text-[16px] leading-4"}>{title}</div>
              {description && (
                <Popover placement="bottom-start">
                  <PopoverHandler>
                    <div>
                      <SvgIcon
                        className="p-0 h-4 w-4 !fill-blue-600 cursor-pointer"
                        iconName="info"
                      />
                    </div>
                  </PopoverHandler>
                  <PopoverContent
                    placeholder=""
                    className="dark:bg-blue-gray-800 border-0 !z-[9999] "
                  >
                    <div className="w-[350px] flex flex-col items-end gap-1">
                      <Typography
                        className="leading-[24px]"
                        color="text-gray-600"
                        variant="Body2-Regular"
                      >
                        {description}
                      </Typography>
                      {link && (
                        <Button
                          variant="text"
                          color="blue"
                          size="xs"
                          onClick={() => showArticle(link.toString())}
                        >
                          Learn more
                          <SvgIcon
                            iconName="arrowLeft"
                            className="rotate-180 w-4 h-4"
                            fill="fill-blue-700"
                          />
                        </Button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <div className="flex-1" />
            {hasControl && (
              <div className="flex gap-4">
                {isFullScreen ||
                  (isUp("2xl") && (
                    <IconButton
                      placeholder=""
                      className="w-[32px] h-[32px]"
                      size="sm"
                      variant="text"
                      onClick={handleMaximize}
                    >
                      <SvgIcon iconName="maximize" fill="fill-gray-600" />
                    </IconButton>
                  ))}

                <IconButton
                  placeholder=""
                  className="w-[32px] h-[32px] drawer-close-button"
                  size="sm"
                  variant="text"
                  onClick={onClose}
                >
                  <SvgIcon iconName="closeSquare" fill="fill-gray-600" />
                </IconButton>
              </div>
            )}
          </div>
        )) ||
          customHeader}

        <div className={classNames("h-[calc(100vh_-_4rem)] overflow-y-auto")}>
          {open && <>{children}</>}
        </div>
      </div>
    </DrawerMui>
  );
}

export default memo(Drawer);
